import {
  IonAccordion,
  IonAccordionGroup,
  IonIcon,
  IonInput,
  IonLabel,
  IonList,
  IonListHeader,
  IonRouterLink,
  IonText,
  useIonAlert,
  useIonViewDidEnter,
} from '@ionic/react';
import { Form, Formik, FormikProps } from 'formik';
import { arrowForwardOutline } from 'ionicons/icons';
import { isEmpty, isEqual, merge } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { usePermissions } from '../../../../../../api/authorization.provider';
import {
  Scratchpad,
  useGetUserQuery,
} from '../../../../../../api/graphql/generated';
import { useHasFeature } from '../../../../../../api/hooks/feature.hooks';
import {
  useGetMachinePreference,
  useSetMachinePreferenceLazy,
} from '../../../../../../api/hooks/preference.hooks';
import { RightAlignedButtons } from '../../../../../../components/button/aligned-buttons';
import { LoadingButton } from '../../../../../../components/button/loading-button';
import { WithConfirmation } from '../../../../../../components/button/with-confirmation';
import { FormSubmitButton } from '../../../../../../components/form/form-submit.button';
import { IonFormTextareaField } from '../../../../../../components/form/ion-form-textarea.field';
import { IonFormField } from '../../../../../../components/form/ion-form.field';
import { IonItem } from '../../../../../../components/ionic/IonItem';
import { useScratchpadServiceContext } from '../../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { FieldLocked } from '../../../../../../components/ui/field-locked';
import {
  trackCreateScratchpad,
  trackUpdatedScratchpad,
} from '../../../../../../config/analytics';
import i18n from '../../../../../../i18n';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { useMachineContext } from '../../../../../../pages/machine/machine-context-provider';
import { isEnter } from '../../../../../../util/keyboard';
import { logger } from '../../../../../../util/logger';
import { QNear, Waypoint } from '../../../../../../util/machine-variable.utils';
import { getUsername } from '../../../../../../util/user-utils';
import { CaptureWaypointButton } from '../../../../shared/component/button/capture-waypoint-button';
import { PickerInput } from '../../../../shared/component/widget/picker-input';
import { SliderInput } from '../../../../shared/component/widget/slider-input';
import { WidgetLabel } from '../../../../shared/component/widget/widget-label';
import { PickerOption } from '../../../../shared/component/widget/widget.types';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import { useToolsNotFoundAlert } from '../../../../shared/hook/part-detail.hooks';
import { Flow } from '../../../../shared/model/flow.types';
import { BeaconCuttingData } from '../../model/beacon-cutting-data';

const scratchpadSchema = yup.object().shape({
  description: yup.string().nullable(),
  name: yup.string().required('*'),
  appId: yup.string().required('*'),
  appData: yup.object().shape({
    config: yup.object().shape({
      betweenSpeed: yup.number().required(),
      home: yup.object().required(),
      ioMapping: yup.object().required(),
      torch: yup.object().required(),
      cutting: yup.object().required(),
      materialType: yup.string().required(),
      pierceHeightHoldTime: yup.number(),
    }),
  }),
  pastedAppDataRef: yup.string(),
});

const APP_DATA_CONFIG_PREFIX = `appData.config`;

export interface CuttingPartDetailProps {
  afterSave?: (scratchpad: Scratchpad) => void;
  readOnly?: boolean;
}

const CuttingPartDetail = (props: CuttingPartDetailProps) => {
  const { afterSave, readOnly } = props;
  const { machine } = useMachineContext();
  const { flowService, flowAnalyticService } =
    useFlowServices<BeaconCuttingData.FlowConfig>();
  const { scratchpad, save, remove, restore } = useScratchpadServiceContext();

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isTogglingActive, setTogglingActive] = useState<boolean>(false);

  const canPasteAppdata = useHasFeature<boolean | undefined>(
    'beacon-scratchpad-paste-appdata'
  );

  const permissions = usePermissions('scratchpad', scratchpad);
  const canDelete = permissions.delete ?? false;

  const canPerformSave =
    scratchpad.id === undefined ? permissions.create : permissions.update;

  const formikRef = useRef<FormikProps<Partial<Scratchpad>> | null>(null);

  const isEditingExisting = scratchpad?.name ? true : false;
  const nameFieldRef = useRef<HTMLIonInputElement>(null);
  const focusName = async () => {
    nameFieldRef.current?.setFocus();
  };

  const createdById = scratchpad.createdBy ?? i18n.t('common:system');
  const updatedById = scratchpad.updatedBy ?? scratchpad.createdBy;

  const { data: getCreatedByUserData } = useGetUserQuery({
    variables: {
      input: {
        id: createdById,
      },
    },
    skip: scratchpad.createdBy === undefined,
    fetchPolicy: 'cache-first',
  });

  const { data: getUpdatedByUserData } = useGetUserQuery({
    variables: {
      input: {
        id: updatedById,
      },
    },
    skip: updatedById === undefined,
    fetchPolicy: 'cache-first',
  });

  const createdByName = getUsername(getCreatedByUserData?.getUser);
  const updatedByName = getUsername(getUpdatedByUserData?.getUser);

  const [showSavePartDefaultsAlert] = useIonAlert();

  const [setPartConfigDefaultPreference] =
    useSetMachinePreferenceLazy<Partial<BeaconCuttingData.FlowConfig>>();

  const defaultPartConfigPreference =
    useGetMachinePreference<BeaconCuttingData.FlowConfig>(
      BeaconCuttingData.MachinePreference.PART_CONFIG_DEFAULTS
    );

  const initialValues: Partial<BeaconCuttingData.CuttingScratchpad> =
    useMemo(() => {
      const preferenceConfig: Partial<BeaconCuttingData.FlowConfig> =
        defaultPartConfigPreference?.value
          ? mapMachinePreferenceFlowConfig(defaultPartConfigPreference.value)
          : {};

      const appData = merge(
        {
          config: flowService.getDefaultFlowConfig(),
        },
        {
          config: preferenceConfig,
        },
        scratchpad.appData
      );

      return {
        ...scratchpad,
        appData,
      };
    }, [flowService, scratchpad, defaultPartConfigPreference]);

  const onSubmit = useCallback(
    async (input: Partial<Scratchpad> & { pastedAppData?: string }) => {
      setIsSaving(true);

      const { pastedAppData, ...partsToSave } = input;

      if (pastedAppData) {
        partsToSave.appData = JSON.parse(pastedAppData);
      }

      const internalSave = async (options?: { savePartDefaults?: boolean }) => {
        const { savePartDefaults } = options ?? { savePartDefaults: false };

        if (savePartDefaults) {
          const config = (partsToSave.appData as Flow)
            ?.config as unknown as BeaconCuttingData.FlowConfig;

          await setPartConfigDefaultPreference({
            key: BeaconCuttingData.MachinePreference.PART_CONFIG_DEFAULTS,
            value: mapMachinePreferenceFlowConfig(config),
          });
        }

        const result = await save(partsToSave as Scratchpad, {
          saveAsDelta: isEditingExisting,
        });

        setIsSaving(false);

        if (result !== undefined) {
          if (input.id) {
            trackUpdatedScratchpad(BeaconCuttingData.APP_SLUG);
          } else {
            trackCreateScratchpad(BeaconCuttingData.APP_SLUG);
          }
        }

        if (result?.id) {
          if (typeof afterSave === 'function') {
            afterSave(result);
          }
        }
      };

      const originalConfig = (
        initialValues.appData as Flow<BeaconCuttingData.FlowConfig>
      ).config;
      const updatedConfig = (partsToSave.appData as Flow).config;

      if (isEqual(originalConfig, updatedConfig)) {
        internalSave();
      } else {
        showSavePartDefaultsAlert(
          getLocalizedText('common:part_config_change') ?? '',
          [
            {
              text: i18n.t('common:yes'),
              handler: () => {
                flowAnalyticService.trackPartSettingsSaveAsDefault(true);
                internalSave({ savePartDefaults: true });
              },
            },
            {
              text: i18n.t('common:no'),
              role: 'cancel',
              handler: () => {
                flowAnalyticService.trackPartSettingsSaveAsDefault(false);
                internalSave();
              },
            },
          ]
        );
      }
    },
    [
      afterSave,
      flowAnalyticService,
      initialValues.appData,
      isEditingExisting,
      save,
      setPartConfigDefaultPreference,
      showSavePartDefaultsAlert,
    ]
  );

  const resetPartSettingsToDefault = () => {
    const setFieldValue = formikRef.current?.setFieldValue;

    if (!setFieldValue) {
      return;
    }

    flowAnalyticService.trackResetPartSettings();

    const defaultConfig = flowService.getDefaultFlowConfig();

    setFieldValue(`${APP_DATA_CONFIG_PREFIX}.torch`, defaultConfig.torch);
    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.materialType`,
      defaultConfig.materialType
    );

    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.home.${BeaconCuttingData.FlowItemParameterRef.WAYPOINT}`,
      defaultConfig.home.waypoint
    );
    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.home.${BeaconCuttingData.FlowItemParameterRef.QNEAR}`,
      defaultConfig.home.qnear
    );
    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.pierceHeightHoldTime`,
      defaultConfig.pierceHeightHoldTime
    );
  };

  const toggleActivation = async () => {
    setTogglingActive(true);

    if (scratchpad.active) {
      logger.info('removing scratchpad', { id: scratchpad.id });
      await remove(scratchpad.id);
    } else {
      logger.info('restoring scratchpad', { id: scratchpad.id });
      await restore(scratchpad.id);
    }

    setTogglingActive(false);
  };

  useIonViewDidEnter(() => {
    if (nameFieldRef.current) {
      focusName();
    }
  }, []);

  const toolOptions: PickerOption[] = useMemo(() => {
    const tools = flowService.getToolsForAssignedEquipment();

    if (tools.length === 0) {
      logger.error('no tools found', {
        machine,
      });
    }

    return tools.map((options) => {
      return {
        label: options.label,
        value: options.config as unknown as Record<string, unknown>,
      };
    });
  }, [flowService, machine]);

  useToolsNotFoundAlert({
    show:
      toolOptions.length === 0 || isEmpty(initialValues?.appData?.config.torch),
  });

  return (
    <Formik
      validationSchema={scratchpadSchema}
      onSubmit={onSubmit}
      initialValues={initialValues}
      enableReinitialize={true}
      validateOnMount={true}
      innerRef={(instance) => (formikRef.current = instance)}
    >
      {({ isValid, dirty, setFieldValue, submitForm }) => {
        return (
          <Form data-cy="scratchpad-form">
            <IonList lines="none">
              <IonFormField
                label={i18n.t('common:scratchpad_name')}
                name="name"
                inputProps={{
                  type: 'text',
                  autocorrect: 'on',
                  autocapitalize: 'on',
                  ref: nameFieldRef,
                  placeholder: i18n.t('common:scratchpad_name_placeholder'),
                  disabled: readOnly,
                  enterkeyhint: 'next',
                  required: true,
                  onKeyDown: (e: React.KeyboardEvent<HTMLIonInputElement>) => {
                    if (isEnter(e)) {
                      submitForm();
                    }
                  },
                }}
              />
              <IonFormTextareaField
                label={i18n.t('common:scratchpad_description')}
                className="form-textarea"
                name="description"
                inputProps={{
                  autocapitalize: 'on',
                  placeholder: i18n.t(
                    'common:scratchpad_description_placeholder'
                  ),
                  disabled: readOnly,
                  enterkeyhint: 'next',
                }}
              />
              {createdByName && (
                <IonItem lines={'full'}>
                  <IonInput
                    labelPlacement={'floating'}
                    type="text"
                    readonly={true}
                    value={createdByName}
                  >
                    <div slot="label">
                      <IonText color="primary">
                        {i18n.t('common:created_by')}{' '}
                        <FieldLocked show={true} />
                      </IonText>
                    </div>
                  </IonInput>
                </IonItem>
              )}
              {updatedByName && (
                <IonItem lines={'full'}>
                  <IonInput
                    labelPlacement={'floating'}
                    type="text"
                    readonly={true}
                    value={updatedByName}
                  >
                    <div slot="label">
                      <IonText color="primary">
                        {i18n.t('common:last_updated_by')}{' '}
                        <FieldLocked show={true} />
                      </IonText>
                    </div>
                  </IonInput>
                </IonItem>
              )}
              {canPasteAppdata && !isEditingExisting && (
                <IonFormTextareaField
                  label={i18n.t('common:scratchpad_app_data') ?? 'App Data'}
                  name="pastedAppData"
                  inputProps={{
                    autoGrow: false,
                    placeholder: `{"items":...}`,
                    disabled: readOnly,
                  }}
                  validate={(value): string | undefined => {
                    if (value === undefined || value === '') {
                      return;
                    }

                    try {
                      JSON.parse(value);
                    } catch (e) {
                      return (
                        i18n.t('common:scratchpad_invalid_json') ??
                        'Invalid JSON'
                      );
                    }
                  }}
                />
              )}
            </IonList>
            <IonList>
              <IonListHeader>
                <IonLabel>{getLocalizedText('common:settings')}</IonLabel>
              </IonListHeader>
              <IonItem className="widget transparent-background" lines="full">
                <WidgetLabel
                  label={getLocalizedText('common:home_waypoint')}
                  forceDisable={readOnly}
                />
                <CaptureWaypointButton
                  cancelButtonText={getLocalizedText(
                    'common:home_waypoint_touch_up_cancel'
                  )}
                  captureButtonText={getLocalizedText(
                    'common:home_waypoint_touch_up'
                  )}
                  onCapture={(waypoint: Waypoint, qnear: QNear) => {
                    setFieldValue(
                      `${APP_DATA_CONFIG_PREFIX}.home.${BeaconCuttingData.FlowItemParameterRef.WAYPOINT}`,
                      waypoint
                    );
                    setFieldValue(
                      `${APP_DATA_CONFIG_PREFIX}.home.${BeaconCuttingData.FlowItemParameterRef.QNEAR}`,
                      qnear
                    );
                  }}
                  disabled={readOnly}
                />
              </IonItem>
              <PickerInput
                name={`${APP_DATA_CONFIG_PREFIX}.torch`}
                options={toolOptions}
                placeholder={getLocalizedText('common:make_selection')}
                label={getLocalizedText('cutting:torch')}
                ionItemProps={{ lines: 'full' }}
                disabled={readOnly}
              />
            </IonList>
            <IonAccordionGroup style={{ paddingBottom: '10px' }}>
              <IonAccordion>
                <IonItem
                  slot="header"
                  style={{ fontWeight: 'bold', fontSize: 'medium' }}
                >
                  <IonLabel>
                    {getLocalizedText('common:advanced_settings')}
                  </IonLabel>
                </IonItem>
                <div slot="content">
                  <IonList>
                    <SliderInput
                      name={`${APP_DATA_CONFIG_PREFIX}.pierceHeightHoldTime`}
                      label={getLocalizedText('cutting:pierceHeightHoldTime')}
                      min={0}
                      max={2}
                      step={0.1}
                      disabled={readOnly}
                    />
                  </IonList>
                  {canPerformSave && (
                    <div className="ion-padding ion-text-center">
                      <IonRouterLink
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();

                          resetPartSettingsToDefault();
                        }}
                      >
                        {getLocalizedText(
                          'common:reset_part_settings_to_default'
                        )}
                      </IonRouterLink>
                    </div>
                  )}
                </div>
              </IonAccordion>
            </IonAccordionGroup>
            {isEditingExisting && !canDelete && (
              <IonLabel
                className="ion-padding content-center ion-text-center"
                color={'medium'}
              >
                {i18n.t('common:deactivate_not_allowed', {
                  item: i18n.t('common:part'),
                })}
              </IonLabel>
            )}
            <RightAlignedButtons>
              {isEditingExisting && (
                <WithConfirmation
                  header={
                    scratchpad.active
                      ? i18n.t('common:scratchpad_deactivate_confirmation')
                      : i18n.t('common:scratchpad_activate_confirmation')
                  }
                >
                  {({ onConfirmed }) => {
                    return (
                      <LoadingButton
                        loading={isTogglingActive}
                        buttonProps={{
                          color: scratchpad.active ? 'danger' : 'primary',
                          disabled:
                            !canDelete ||
                            isSaving ||
                            isTogglingActive ||
                            machine?.active === false ||
                            readOnly,
                          fill: 'outline',
                          onClick: () =>
                            onConfirmed({ onConfirmation: toggleActivation }),
                        }}
                      >
                        {scratchpad.active
                          ? i18n.t('common:scratchpad_deactivate')
                          : i18n.t('common:scratchpad_activate')}
                      </LoadingButton>
                    );
                  }}
                </WithConfirmation>
              )}
              {canPerformSave && (
                <FormSubmitButton
                  loading={isSaving}
                  disabled={
                    !isValid ||
                    !dirty ||
                    isSaving ||
                    isTogglingActive ||
                    readOnly
                  }
                >
                  {isEditingExisting ? (
                    i18n.t('common:save')
                  ) : (
                    <>
                      {i18n.t('common:next')}
                      <IonIcon icon={arrowForwardOutline} slot="end" />
                    </>
                  )}
                </FormSubmitButton>
              )}
            </RightAlignedButtons>
          </Form>
        );
      }}
    </Formik>
  );
};

const mapMachinePreferenceFlowConfig = (
  config: Partial<BeaconCuttingData.FlowConfig>
): Partial<BeaconCuttingData.FlowConfig> => {
  return {
    home: config.home,
    torch: config.torch,
    pierceHeightHoldTime: config.pierceHeightHoldTime,
  };
};

export { CuttingPartDetail };
